import { logger } from '@adornis/base/logging.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getFileByIDQuery } from '../../../../../../db/api/get-all-files.js'; // Pfad ggf. anpassen
import {
  SUBMISSION_ASSIGNMENT_INVALID,
  type SubmissionAssignment,
  type SubmissionAssignmentEntry,
} from '../../../../../../db/api/save-submission-assignment.js';
import type { DragItem, OrderedPage, ReviewSubmission, SubmissionReviewProps } from '../utils.js';
import { UNASSIGNED_BUCKET_ID } from '../utils.js';
import { usePreviewLightbox } from './use-preview-lightbox.js';

// Changes in quick succession (typing a name, several drops) go out as one save.
const AUTOSAVE_DELAY_MS = 600;

/**
 * `unsaved`: pages lie in the Ablage — every page must belong to exactly one student before the assignment can be
 * saved. `failed`: the last save failed; the next change tries again. `reloaded`: the server refused the assignment
 * because the submissions changed meanwhile, so the canvas shows the stored state again.
 */
export type ReviewSaveStatus = 'saved' | 'saving' | 'unsaved' | 'failed' | 'reloaded';

function isNameRelatedSplitIssue(issue: string): boolean {
  const normalized = issue.trim().toLowerCase();
  return (
    normalized.includes('kein eindeutiger schülername gefunden') || normalized.includes('schülername ist unsicher')
  );
}

export function useSubmissionState(props: SubmissionReviewProps) {
  const { submissions, onSave, lockingEnabled = false } = props;

  // Individually locked submissions (confirmed batch). New/unsaved buckets and the unassigned
  // bucket are never locked.
  const lockedIds = useMemo(
    () => new Set(lockingEnabled ? submissions.filter(s => s.splitConfirmed).map(s => s._id) : []),
    [submissions, lockingEnabled],
  );
  const isBucketLocked = (bucketId: string) => lockedIds.has(bucketId);

  // The whole canvas is read-only only once every submission is confirmed (nothing left to edit).
  const isReadOnly = lockingEnabled && submissions.length > 0 && submissions.every(s => s.splitConfirmed);

  const [pagesByStudent, setPagesByStudent] = useState<Record<string, OrderedPage[]>>({});
  const [editedNames, setEditedNames] = useState<Record<string, string>>({});
  const [splitConfidenceByStudent, setSplitConfidenceByStudent] = useState<Record<string, number>>({});
  const [splitIssuesByStudent, setSplitIssuesByStudent] = useState<Record<string, string[]>>({});
  const [newStudents, setNewStudents] = useState<Record<string, { tempId: string; name: string }>>({});
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  const [fallbackUrls, setFallbackUrls] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<ReviewSaveStatus>('saved');

  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const [dropTarget, setDropTarget] = useState<DragItem | null>(null);

  // Every edit bumps the revision; `savedRevisionRef` is the one the server has. While they differ, or a save is
  // running, the canvas does not take over `submissions` from the server: the live subscription reloads them on any
  // change of any submission, which used to wipe the edits.
  const [revision, setRevision] = useState(0);
  const revisionRef = useRef(0);
  const savedRevisionRef = useRef(0);
  const saveInFlightRef = useRef(false);
  // New students the server has created already: temp bucket id → submission id, until the canvas takes over the
  // stored submissions again.
  const createdIdsRef = useRef(new Map<string, string>());
  // Placeholders shown for submissions without a stored name ("Schüler 3"): never sent as the name.
  const placeholderNamesRef = useRef<Record<string, string>>({});
  const submissionsRef = useRef(submissions);
  const onSaveRef = useRef(onSave);
  const buildAssignmentRef = useRef<() => SubmissionAssignment | null>(() => null);

  const lightbox = usePreviewLightbox(pagesByStudent, fallbackUrls);

  const markChanged = () => setRevision(current => current + 1);
  const isDirty = () => revisionRef.current !== savedRevisionRef.current;

  const initFrom = (source: ReviewSubmission[]) => {
    const initialBuckets: Record<string, OrderedPage[]> = { [UNASSIGNED_BUCKET_ID]: [] };
    const initialNames: Record<string, string> = {};
    const placeholderNames: Record<string, string> = {};
    const initialSplitConfidence: Record<string, number> = {};
    const initialSplitIssues: Record<string, string[]> = {};

    source.forEach((sub, index) => {
      const normalizedName = sub.studentName?.trim();
      const placeholder = sub.studentId ? `Student ${sub.studentId}` : `Schüler ${index + 1}`;
      initialNames[sub._id] = normalizedName || placeholder;
      if (!normalizedName) placeholderNames[sub._id] = placeholder;
      initialSplitConfidence[sub._id] =
        typeof sub.splitEvaluationResult?.confidence === 'number' ? sub.splitEvaluationResult.confidence : 0;
      initialSplitIssues[sub._id] = Array.isArray(sub.splitEvaluationResult?.issues)
        ? sub.splitEvaluationResult.issues.map(issue => String(issue)).filter(issue => !isNameRelatedSplitIssue(issue))
        : [];

      const imageIds = sub.imageIds || [];
      const urls = sub.pages || [];

      initialBuckets[sub._id] = imageIds.map((imgId, i) => ({
        imageId: imgId,
        url: urls[i] || '',
        sourceSubmissionId: sub._id,
      }));
    });

    setPagesByStudent(initialBuckets);
    setEditedNames(initialNames);
    placeholderNamesRef.current = placeholderNames;
    setSplitConfidenceByStudent(initialSplitConfidence);
    setSplitIssuesByStudent(initialSplitIssues);
    // The stored submissions include the ones created and lack the ones deleted by earlier saves.
    setNewStudents({});
    setDeletedIds(new Set());
    createdIdsRef.current = new Map();
  };

  useEffect(() => {
    submissionsRef.current = submissions;
    if (saveInFlightRef.current || isDirty()) return;
    initFrom(submissions);
  }, [submissions]);

  useEffect(() => {
    revisionRef.current = revision;
  }, [revision]);

  useEffect(() => {
    onSaveRef.current = onSave;
  });

  // Load Fallback URLs
  useEffect(() => {
    const allPages = Object.values(pagesByStudent).flat();
    const missingImageIds = Array.from(new Set(allPages.filter(p => !p.url && p.imageId).map(p => p.imageId)));
    if (missingImageIds.length === 0) return;

    let isMounted = true;
    const loadUrls = async () => {
      const results = await Promise.allSettled(
        missingImageIds.map(async id => {
          const file = await getFileByIDQuery(id)({ _id: 1, meta: { fileName: 1, extension: 1 } });
          return { id, url: file.getServeLink() };
        }),
      );
      if (!isMounted) return;
      setFallbackUrls(prev => {
        const next = { ...prev };
        results.forEach(r => {
          if (r.status === 'fulfilled') next[r.value.id] = r.value.url;
        });
        return next;
      });
    };
    void loadUrls();
    return () => {
      isMounted = false;
    };
  }, [pagesByStudent]);

  // Drag and Drop Logik
  const handleDragStart = (e: React.DragEvent, bucketId: string, index: number) => {
    if (isReadOnly || isBucketLocked(bucketId)) return;
    setDraggedItem({ bucketId, index });
    e.dataTransfer.effectAllowed = 'move';
    const crt = (e.currentTarget as HTMLElement).cloneNode(true) as HTMLElement;
    crt.style.opacity = '0.5';
    document.body.appendChild(crt);
    e.dataTransfer.setDragImage(crt, 20, 20);
    setTimeout(() => document.body.removeChild(crt), 0);
  };

  const handleDragOver = (e: React.DragEvent, bucketId: string, index: number) => {
    if (isReadOnly || isBucketLocked(bucketId)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTarget?.bucketId !== bucketId || dropTarget?.index !== index) {
      setDropTarget({ bucketId, index });
    }
  };

  const handleDrop = (e: React.DragEvent, targetBucketId: string, targetIndex: number) => {
    // Strict freeze: can't drop into a locked bucket, nor pull a page out of one.
    if (isReadOnly || isBucketLocked(targetBucketId) || (draggedItem && isBucketLocked(draggedItem.bucketId))) return;
    e.preventDefault();
    if (!draggedItem) return;

    setPagesByStudent(prev => {
      const next = { ...prev };
      const sourceList = [...(next[draggedItem.bucketId] || [])];
      const targetList = draggedItem.bucketId === targetBucketId ? sourceList : [...(next[targetBucketId] || [])];

      const [movedPage] = sourceList.splice(draggedItem.index, 1);
      if (!movedPage) return prev;

      let finalIndex = targetIndex;
      if (draggedItem.bucketId === targetBucketId && draggedItem.index < targetIndex) {
        finalIndex -= 1;
      }

      if (finalIndex === -1) targetList.push(movedPage);
      else targetList.splice(finalIndex, 0, movedPage);

      next[draggedItem.bucketId] = sourceList;
      next[targetBucketId] = targetList;
      return next;
    });
    markChanged();

    setDraggedItem(null);
    setDropTarget(null);
  };

  // Actions
  // An empty new student has nothing to store; it is saved once it gets a page.
  const addNewStudent = () => {
    if (isReadOnly) return;
    const tempId = `new-${Date.now()}`;
    const studentCount = submissions.length + Object.keys(newStudents).length + 1;
    setNewStudents(prev => ({ ...prev, [tempId]: { tempId, name: `Neuer Schüler ${studentCount}` } }));
    setPagesByStudent(prev => ({ ...prev, [tempId]: [] }));
  };

  const deleteStudent = (bucketId: string, isNew: boolean) => {
    if (isReadOnly || isBucketLocked(bucketId)) return;
    setPagesByStudent(prev => {
      const next = { ...prev };
      const orphanedPages = next[bucketId] || [];
      next[UNASSIGNED_BUCKET_ID] = [...(next[UNASSIGNED_BUCKET_ID] || []), ...orphanedPages];
      delete next[bucketId];
      return next;
    });

    if (isNew) {
      setNewStudents(prev => {
        const n = { ...prev };
        delete n[bucketId];
        return n;
      });
    }
    // A new student the server already created is deleted like any other.
    const submissionId = isNew ? createdIdsRef.current.get(bucketId) : bucketId;
    if (submissionId) setDeletedIds(prev => new Set(prev).add(submissionId));
    markChanged();
  };

  const unassignedPages = pagesByStudent[UNASSIGNED_BUCKET_ID] || [];

  // Only submissions the canvas has loaded: one that arrived since (a split finishing) is not in `pagesByStudent`
  // yet, stays out of the saves and shows up once the canvas takes over the stored submissions again.
  const knownSubmissions = submissions.filter(s => !deletedIds.has(s._id) && s._id in pagesByStudent);

  const buildAssignment = (): SubmissionAssignment | null => {
    if (unassignedPages.length > 0) return null;
    const pagesOf = (bucketId: string) => pagesByStudent[bucketId]?.map(p => p.imageId) || [];
    const entries: SubmissionAssignmentEntry[] = knownSubmissions.map(s => {
      const name = editedNames[s._id] ?? '';
      return {
        submissionId: s._id,
        studentName: name === placeholderNamesRef.current[s._id] ? '' : name,
        imageIds: pagesOf(s._id),
      };
    });
    for (const [tempId, data] of Object.entries(newStudents)) {
      const imageIds = pagesOf(tempId);
      const submissionId = createdIdsRef.current.get(tempId);
      if (submissionId) entries.push({ submissionId, studentName: data.name, imageIds });
      else if (imageIds.length > 0) entries.push({ tempId, studentName: data.name, imageIds });
    }
    return { submissions: entries, deleteSubmissionIds: [...deletedIds] };
  };

  useEffect(() => {
    buildAssignmentRef.current = buildAssignment;
  });

  // One save at a time; changes made while it runs go out in the next one. Reads only refs, so it also finishes the
  // last save after the canvas was closed.
  const flushSave = () => {
    if (saveInFlightRef.current || !isDirty()) return;
    const assignment = buildAssignmentRef.current();
    if (!assignment) {
      setSaveStatus('unsaved');
      return;
    }

    const savingRevision = revisionRef.current;
    const tempIds = assignment.submissions.flatMap(entry => (entry.tempId ? [entry.tempId] : []));
    saveInFlightRef.current = true;
    setSaveStatus('saving');

    void onSaveRef
      .current(assignment)
      .then(createdIds => {
        tempIds.forEach((tempId, i) => {
          const submissionId = createdIds[i];
          if (submissionId) createdIdsRef.current.set(tempId, submissionId);
        });
        savedRevisionRef.current = savingRevision;
        saveInFlightRef.current = false;
        if (isDirty()) flushSave();
        else setSaveStatus('saved');
      })
      .catch(err => {
        saveInFlightRef.current = false;
        logger.error({ err, context: 'useSubmissionState' }, 'Saving the submission assignment failed');
        if (String((err as { message?: unknown })?.message ?? err).includes(SUBMISSION_ASSIGNMENT_INVALID)) {
          // Built on submissions that changed meanwhile: it cannot be saved, so the stored state is shown again.
          savedRevisionRef.current = revisionRef.current;
          initFrom(submissionsRef.current);
          setSaveStatus('reloaded');
          return;
        }
        setSaveStatus('failed');
      });
  };

  useEffect(() => {
    if (isReadOnly || !isDirty()) return;
    if (unassignedPages.length > 0) {
      setSaveStatus('unsaved');
      return;
    }
    setSaveStatus('saving');
    const timer = setTimeout(flushSave, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [revision, unassignedPages.length]);

  // Closing the canvas or switching the view: what is not saved yet goes out now (the timer above is cleared first).
  useEffect(() => () => flushSave(), []);

  const allBuckets = useMemo(() => {
    const original = knownSubmissions.map(s => ({ id: s._id, isNew: false, locked: lockedIds.has(s._id) }));
    const added = Object.keys(newStudents).map(id => ({ id, isNew: true, locked: false }));
    return [...original, ...added];
  }, [submissions, deletedIds, newStudents, lockedIds, pagesByStudent]);

  const updateNewStudentName = (bucketId: string, name: string) => {
    if (isReadOnly) return;
    setNewStudents(prev => ({
      ...prev,
      [bucketId]: { tempId: prev[bucketId]?.tempId || bucketId, name },
    }));
    markChanged();
  };

  const updateEditedName = (bucketId: string, name: string) => {
    if (isReadOnly || isBucketLocked(bucketId)) return;
    setEditedNames(prev => ({ ...prev, [bucketId]: name }));
    markChanged();
  };

  return {
    state: {
      pagesByStudent,
      editedNames,
      splitConfidenceByStudent,
      splitIssuesByStudent,
      newStudents,
      fallbackUrls,
      saveStatus,
      isReadOnly,
      draggedItem,
      dropTarget,
      allBuckets,
      unassignedPages,
    },
    actions: {
      handleDragStart,
      handleDragOver,
      handleDrop,
      addNewStudent,
      deleteStudent,
      updateEditedName,
      updateNewStudentName,
    },
    lightbox,
  };
}

export type SubmissionReviewStore = ReturnType<typeof useSubmissionState>;
